import { Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import React, { PropsWithChildren, ReactNode, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Label, OptionSelection, Space } from '@/components';
import { MainLayout } from '@/layout';
import { DemoUIState } from '@/model/demo/types';
import { makeSelectIsMobileView } from '@/model/global/selectors';
import { storeCleanWithdrawal } from '@/model/withdrawal/actions';

import { ReactComponent as BillIcon } from './icons/bill.svg';
import { ReactComponent as CardIcon } from './icons/card.svg';
import { ReactComponent as PhoneIcon } from './icons/phone.svg';
import { ReactComponent as QRIcon } from './icons/qr.svg';
import { ReactComponent as TemplateIcon } from './icons/template.svg';
import { ReactComponent as WalletExtIcon } from './icons/walletExt.svg';
import { ReactComponent as WalletIntIcon } from './icons/walletInt.svg';

const useStyles = makeStyles((theme: Theme) => ({
  containerMobile: {
    backgroundColor: theme.colors.light,
  },
}));

interface OptionsBlockProps extends PropsWithChildren<unknown> {
  title: ReactNode;
}

export const OptionsBlock: React.FC<OptionsBlockProps> = ({ title, children }: OptionsBlockProps) => (
  <Space size={1.5} direction="vertical" style={{ flexShrink: 0 }}>
    <Label>{title}</Label>
    {children}
  </Space>
);

const selectIsMobile = makeSelectIsMobileView();

export const SendOptionsPage: React.FC = () => {
  const history = useHistory();
  const classes = useStyles();
  const isMobile = useSelector(selectIsMobile);
  const dispatch = useDispatch();

  const transfer = useCallback(async () => {
    await storeCleanWithdrawal()(dispatch);
    history.push(`${DemoUIState.RECEIVER}`);
  }, [dispatch, history]);

  return (
    <MainLayout
      minHeight={400}
      height={660}
      content={{ className: isMobile ? classes.containerMobile : undefined }}
      buttons={{
        back: `${DemoUIState.WALLET}`,
      }}
      header={{
        title: <FormattedMessage id="demo.pages.sendOptions.title" />,
      }}
    >
      <OptionsBlock title={<FormattedMessage id="demo.pages.sendOptions.payments.title" />}>
        <OptionSelection
          startIcon={<QRIcon />}
          disabled
          title={<FormattedMessage id="demo.pages.sendOptions.payments.qr" />}
        />
        <OptionSelection
          startIcon={<BillIcon />}
          disabled
          title={<FormattedMessage id="demo.pages.sendOptions.payments.bill" />}
        />
        <OptionSelection
          startIcon={<TemplateIcon />}
          disabled
          title={<FormattedMessage id="demo.pages.sendOptions.payments.template" />}
        />
      </OptionsBlock>
      <OptionsBlock title={<FormattedMessage id="demo.pages.sendOptions.transfer.title" />}>
        <OptionSelection
          onSelect={transfer}
          startIcon={<PhoneIcon />}
          title={<FormattedMessage id="demo.pages.sendOptions.transfer.phone" />}
        />
        <OptionSelection
          startIcon={<WalletExtIcon />}
          disabled
          title={<FormattedMessage id="demo.pages.sendOptions.transfer.walletExt" />}
        />
        <OptionSelection
          startIcon={<WalletIntIcon />}
          disabled
          title={<FormattedMessage id="demo.pages.sendOptions.transfer.walletInt" />}
        />
        <OptionSelection
          startIcon={<CardIcon />}
          disabled
          title={<FormattedMessage id="demo.pages.sendOptions.transfer.card" />}
        />
      </OptionsBlock>
    </MainLayout>
  );
};

export default SendOptionsPage;
